// @flow

import type {PartnerEvidence} from '@containers/disputes/types';

import {useEffect, useMemo, useState} from 'react';
import {getPartnerEvidence} from '@api/disputes';

type Args = {
  merchantId?: ?number,
  disputeId?: ?number,
  enabled?: boolean
};

type Result = {
  partnerEvidence: ?PartnerEvidence,
  isLoading: boolean,
  error: ?Error
};

const pendingRequests: {[string]: Promise<PartnerEvidence>} = {};

const getCacheKey = (merchantId?: ?number, disputeId?: ?number): string =>
  `${merchantId || 'unknown'}:${disputeId || 'none'}`;

export const usePartnerEvidence = ({merchantId, disputeId, enabled = true}: Args): Result => {
  const cacheKey = useMemo(() => getCacheKey(merchantId, disputeId), [merchantId, disputeId]);
  const [partnerEvidence, setPartnerEvidence] = useState<?PartnerEvidence>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(enabled && merchantId && disputeId));
  const [error, setError] = useState<?Error>(null);

  useEffect(() => {
    if (!enabled || !merchantId || !disputeId) {
      return undefined;
    }

    let cancelled = false;
    const pendingRequest = pendingRequests[cacheKey];
    const request = pendingRequest || getPartnerEvidence(merchantId, disputeId);

    if (!pendingRequest) {
      pendingRequests[cacheKey] = request;
    }

    setIsLoading(true);

    request
      .then(result => {
        if (!cancelled) {
          setPartnerEvidence(result);
          setError(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err);
        }
      })
      .finally(() => {
        if (pendingRequests[cacheKey] === request) {
          delete pendingRequests[cacheKey];
        }
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, merchantId, disputeId, enabled]);

  return {partnerEvidence, isLoading, error};
};
